import { useEffect, useState } from 'react'
import { usePublicClient } from 'wagmi'
import { ADDRESSES, IS_LIVE } from '@/lib/contracts'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import { getLogsChunked } from '@/lib/getLogsChunked'

const BORROWED_EVENT = poolAbi.find((x) => x.type === 'event' && x.name === 'Borrowed')
const SUPPLIED_EVENT = poolAbi.find((x) => x.type === 'event' && x.name === 'Supplied')

/// Every address that's ever borrowed or supplied against `HorizonLendingPool`, across every
/// reserve - so the Liquidations page has something to render. There's no on-chain enumeration
/// of `userReserves`/`userConfig` (they're plain mappings), so this reconstructs the set from
/// indexed event logs instead, the same way a subgraph would, just done client-side. A single
/// scan covers every reserve (the retired two-market design needed two separate scans, one per
/// contract) since supply/borrow on any asset now goes through this one contract. Scans from
/// `LENDING_POOL_DEPLOY_BLOCK`, not literal block 0, via `getLogsChunked` - windowed into
/// provider-sized chunks rather than one wide call, since a genesis-to-latest scan would
/// otherwise fail outright once real chain history exceeds an RPC provider's `eth_getLogs` range
/// cap (commonly 10-50k blocks; forked chains inherit the real chain's full height, so this bites
/// even on a fork despite the contract's own history being only a few blocks long). Still doesn't
/// fix the "re-scans everything, gets slower as history grows" cost that a real indexer would -
/// fine at this protocol's expected scale, not something that'd hold up indefinitely.
export function useAllBorrowers() {
  const client = usePublicClient()
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!IS_LIVE || !client) { setLoading(false); return }
    let cancelled = false

    async function run() {
      setLoading(true)
      try {
        const fromBlock = ADDRESSES.LENDING_POOL_DEPLOY_BLOCK ? BigInt(ADDRESSES.LENDING_POOL_DEPLOY_BLOCK) : 0n
        const [borrowed, supplied] = await Promise.all([
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: BORROWED_EVENT, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: SUPPLIED_EVENT, fromBlock }),
        ])
        const set = new Set()
        for (const log of borrowed) set.add(log.args.user)
        for (const log of supplied) set.add(log.args.user)
        if (!cancelled) setAddresses([...set])
      } catch (e) {
        console.error('useAllBorrowers: getLogs scan failed', e)
        if (!cancelled) setAddresses([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [client])

  return { addresses, loading }
}
